import {
  createFileRoute,
  Link,
  useRouter,
  useLocation,
  useLoaderData,
  notFound,
  redirect,
} from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";
import { useState, useMemo, useEffect, useRef } from "react";
import { ImprintPopUp } from "~/components/PopUp/ImprintPopUp";
import { AmenitySection } from "~/components/AmenitySection";
import { BadgeList } from "~/components/BadgeList";
import { ContentSection } from "~/components/ContentElements/ContentSection";
import { ImageGrid } from "~/components/ContentElements/ImageGrid";
import { SectionNav } from "~/components/ContentElements/SectionNav";
import { SubSection } from "~/components/ContentElements/SubSection";
import { DistanceTable } from "~/components/DistanceTable";
import { FoldableBlock } from "~/components/Hotel/FoldableBlock";
import { HotelDetail } from "~/components/Hotel/HotelDetail";
import { HotelRoomCard } from "~/components/Hotel/HotelRoomCard";
import { LocationActions } from "~/components/Location/LocationActions";
import { LocationContact } from "~/components/Location/LocationContact";
import { LocationDetail } from "~/components/Location/LocationDetail";
import { LocationSeatingCard } from "~/components/Location/LocationSeatingCard";
import { RoomCard } from "~/components/Room/RoomCard";
import { OutsideAreaCard } from "~/components/OutsideArea/OutsideAreaCard";
import { ScheduleTable } from "~/components/ScheduleTable";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Badge } from "~/components/ui/badge";
import { CellularIcon } from "~/icons/CellularIcon";
import { CheckIcon } from "~/icons/CheckIcon";
import { CutleryIcon } from "~/icons/CutleryIcon";
import { GearIcon } from "~/icons/GearIcon";
import { Lamp2Icon } from "~/icons/Lamp2Icon";
import { PowerIcon } from "~/icons/PowerIcon";
import { RadioIcon } from "~/icons/RadioIcon";
import { StageIcon } from "~/icons/StageIcon";
import { WifiIcon } from "~/icons/WifiIcon";
import { XIcon } from "~/icons/XIcon";
import iconAirport from "~/assets/distances/airport.png";
import iconCityCenter from "~/assets/distances/city-center.png";
import iconRailstation from "~/assets/distances/railstation.png";
import iconMotorway from "~/assets/distances/motorway.png";
import iconPublicTransport from "~/assets/distances/public-transport.png";
import {
  getLocation,
  groupImagesByCategory,
} from "~/lib/server-functions/get-locations";
import {
  filterPublicGalleryImages,
  getPlanLimits,
  limitImagesByPlan,
} from "~/lib/plans/plan-limits";
import {
  useAddLocationToWatchListMutation,
  useRemoveLocationFromWatchListMutation,
} from "~/lib/tanstack-query/hooks/mutations/watchlist";
import { useGetLocationFromWatchList } from "~/lib/tanstack-query/hooks/useWatchlist";
import { FaqSection } from "~/components/FaqSection";
import { DownloadSection } from "~/components/DownloadSection";
import { openInquiry } from "~/lib/stores/inquiry";
import { useSetInquiryLocationMutation } from "~/lib/tanstack-query/hooks/mutations/inquiryLocations";
import { RichTextRenderer } from "~/components/RichTextRenderer";
import {
  ShareDialog,
  type ShareDialogProps,
} from "~/components/Location/ShareDialog";
import { formatGermanCurrency } from "~/components/ui/price-input";
import { Button } from "~/components/ui/button";
import { getStateLabel } from "~/lib/add-location/state-options";
import {
  formatGermanNumberWithoutDecimals,
  resolveAssetUrl,
  convertCmToMeters,
} from "~/lib/utils";
import {
  getLocationPath,
  LOCATION_SECTION_IDS,
} from "~/lib/utils/location-path";
import { withLocale } from "~/lib/utils/with-locale";
import { useAllWatchList } from "~/lib/tanstack-query/hooks/useWatchlist";
import { HeartFilledIcon } from "~/icons/HeartFilledIcon";

function formatTimeForDisplay(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const hour = h ?? 0;
  const minute = m ?? 0;

  if (hour >= 24 && hour <= 27) {
    const nextDayHour = hour - 24;
    const mm = String(minute).padStart(2, "0");
    return `${String(nextDayHour).padStart(2, "0")}:${mm}`;
  }

  return time;
}

function formatOpeningHours(hours: any[]) {
  const dayNames = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

  const result: Record<string, { timeframes: { from: string; to: string }[] }> =
    {};

  for (const h of hours) {
    const key = dayNames[h.weekday];
    if (!key || !h.fromTime || !h.untilTime) continue;

    if (!result[key]) {
      result[key] = { timeframes: [] };
    }

    result[key].timeframes.push({
      from: formatTimeForDisplay(h.fromTime),
      to: formatTimeForDisplay(h.untilTime),
    });
  }

  return Object.keys(result).length > 0 ? result : undefined;
}

type LocationSearch = {
  previewPlan?: "superior" | "premium" | "basic";
};

export const Route = createFileRoute("/__main/location/$id")({
  validateSearch: (search: Record<string, unknown>): LocationSearch => {
    const validPlans = ["superior", "premium", "basic"];
    const previewPlan = search.previewPlan as string | undefined;
    return {
      previewPlan:
        previewPlan && validPlans.includes(previewPlan)
          ? (previewPlan as LocationSearch["previewPlan"])
          : undefined,
    };
  },
  component: function Redirecting() {
    return null;
  },
  loader: async ({ params, location }) => {
    const id = parseInt(params.id, 10);
    if (Number.isNaN(id)) {
      throw notFound();
    }
    const locationData = await getLocation({ data: id });
    if (!locationData) {
      throw notFound();
    }
    const canonicalPath = getLocationPath(locationData, 0);
    const searchParams = new URLSearchParams(location.search);
    const previewPlan = searchParams.get("previewPlan");
    const validPlans = ["superior", "premium", "basic"];
    const search =
      previewPlan && validPlans.includes(previewPlan)
        ? { previewPlan: previewPlan as LocationSearch["previewPlan"] }
        : undefined;
    throw redirect({ to: withLocale(canonicalPath), search });
  },
});

export type LocationPageLoaderData = {
  location: Awaited<ReturnType<typeof getLocation>>;
  previewPlan?: "superior" | "premium" | "basic";
  isPreviewMode?: boolean;
  initialTab?: number;
};

export function LocationPageContent({
  loaderData,
}: {
  loaderData: LocationPageLoaderData;
}) {
  const { location, previewPlan, isPreviewMode, initialTab } = loaderData;
  const { hash: urlHash } = useLocation();
  const [imprintOpen, setImprintOpen] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showActionsInNav, setShowActionsInNav] = useState(false);
  const sidebarActionsRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const { data: watchListLocations } = useAllWatchList();

  // Show compact action buttons in subnav when sidebar actions have scrolled out of view (desktop only)
  useEffect(() => {
    const el = sidebarActionsRef.current;
    if (!el) return;
    const mq = window.matchMedia("(min-width: 768px)");
    const update = (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0];
      if (!entry) return;
      if (mq.matches) setShowActionsInNav(!entry.isIntersecting);
      else setShowActionsInNav(false);
    };
    const observer = new IntersectionObserver(update, { threshold: 0 });
    observer.observe(el);
    const onResize = () => {
      if (!mq.matches) setShowActionsInNav(false);
    };
    mq.addEventListener("change", onResize);
    return () => {
      observer.disconnect();
      mq.removeEventListener("change", onResize);
    };
  }, []);

  // Reserve space for the mobile sticky bottom action bar so it doesn't cover the footer/content.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");

    const update = () => {
      document.documentElement.style.setProperty(
        "--location-mobile-actions-height",
        mq.matches ? "calc(80px + env(safe-area-inset-bottom))" : "0px",
      );
    };

    update();
    mq.addEventListener("change", update);
    return () => {
      mq.removeEventListener("change", update);
      document.documentElement.style.setProperty(
        "--location-mobile-actions-height",
        "0px",
      );
    };
  }, []);

  // Scroll to section when loading the page with a hash or initialTab (e.g. #downloads or tab=1)
  // Skip scroll when target is overview and no hash — keeps page at top for default /0 links
  useEffect(() => {
    const hash =
      urlHash?.replace("#", "") ||
      (typeof window !== "undefined" ? window.location.hash?.slice(1) : "");
    const sectionFromTab =
      initialTab != null && LOCATION_SECTION_IDS[initialTab]
        ? LOCATION_SECTION_IDS[initialTab]
        : null;
    const targetSection = hash || sectionFromTab;
    if (!targetSection) return;
    if (targetSection === "overview" && !hash) return;
    const scrollToTarget = () => {
      const el = document.getElementById(targetSection);
      if (el) {
        el.scrollIntoView({ behavior: "auto", block: "start" });
      }
      if (sectionFromTab && !hash && typeof window !== "undefined") {
        window.history.replaceState(
          null,
          "",
          `${window.location.pathname}#${targetSection}`,
        );
      }
    };
    // Defer so the target section is in the DOM (conditionally rendered sections)
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(scrollToTarget);
    });
    return () => cancelAnimationFrame(rafId);
  }, [urlHash, initialTab]);

  // Use preview plan if available (and authorized), otherwise use actual plan
  const effectivePlan = previewPlan ?? location?.plan ?? null;
  const planLimits = getPlanLimits(effectivePlan);

  // Main gallery is merged (location + rooms + hotel). Apply single plan limit.
  const filteredImages = useMemo(
    () => filterPublicGalleryImages(location?.images ?? []),
    [location?.images],
  );

  // The editor persists the preview image by setting its `sort` to 0 (and `getLocation()`
  // loads images ordered by sort). So the first filtered image is the preview image.
  const primaryImageId = filteredImages[0]?.id ?? null;

  const limitedImages = useMemo(
    () => limitImagesByPlan(location?.images ?? [], effectivePlan),
    [location?.images, effectivePlan],
  );

  const groupedLocationImages = groupImagesByCategory(limitedImages, {
    excludePurpose: ["location_logo", "location_contact_person_image"],
  });

  // Videos attached to the location (up to 3), for display between Räume and Detaildaten
  const locationVideos = useMemo(() => {
    const images = location?.images ?? [];
    return images
      .filter(
        (img) =>
          img.purpose === "location_video" ||
          img.mimetype?.startsWith("video/"),
      )
      .slice(0, 3)
      .map((img) => ({
        id: img.id,
        path: img.path ?? "",
        mimetype: img.mimetype ?? "",
      }));
  }, [location?.images]);

  const { data: watchListEntry } = useGetLocationFromWatchList(location?.id);

  const addLocationToWatchListMutation = useAddLocationToWatchListMutation();
  const removeLocationFromWatchListMutation =
    useRemoveLocationFromWatchListMutation();
  const { mutateAsync: setInquiryLocation } = useSetInquiryLocationMutation();

  const shareUrl = useMemo(
    () =>
      location && typeof window !== "undefined"
        ? `${window.location.origin}${withLocale(getLocationPath(location))}`
        : "",
    [location],
  );

  const infrastructureItems =
    location?.infrastructure?.map((i) => {
      let icon;
      switch (i.type) {
        case "airport":
          icon = <img src={iconAirport} alt="" />;
          break;
        case "railstation":
          icon = <img src={iconRailstation} alt="" />;
          break;
        case "motorway":
          icon = <img src={iconMotorway} alt="" />;
          break;
        case "city-center":
          icon = <img src={iconCityCenter} alt="" />;
          break;
        case "public-transport":
          icon = <img src={iconPublicTransport} alt="" />;
          break;
        default:
          icon = null;
      }

      const rawMeters = i.distance ?? 0;
      const unit = i.distanceUnit === "m" ? "m" : "km";
      const displayDistance = unit === "km" ? rawMeters / 1000 : rawMeters;

      return {
        icon,
        label: i.name,
        abbreviation: i.abbreviation ?? "",
        distance:
          displayDistance.toLocaleString("de-DE", {
            maximumFractionDigits: unit === "km" ? 2 : 0,
          }) ?? "",
        unit,
      };
    }) ?? [];

  const logo = location?.images.find(
    (image) => image.purpose === "location_logo",
  );

  const insideAreaSum =
    location?.insideAreaSum && location.insideAreaSum > 0
      ? convertCmToMeters(location.insideAreaSum)
      : location?.indoorAreaTotal && location.indoorAreaTotal > 0
        ? convertCmToMeters(location.indoorAreaTotal)
        : 0;

  const outsideAreaSum =
    location?.outsideAreaSum && location.outsideAreaSum > 0
      ? convertCmToMeters(location.outsideAreaSum)
      : 0;

  // Calculate totalRooms from actual rooms relation (totalRooms field is deprecated)
  const totalRooms = location?.rooms?.length ?? 0;

  const capacityStanding =
    location?.capacityStanding && location.capacityStanding > 0
      ? location.capacityStanding
      : location?.seatingStanding && location.seatingStanding > 0
        ? location.seatingStanding
        : 0;

  const biggestRoom = location
    ? location?.biggestRoom && location.biggestRoom > 0
      ? location.biggestRoom
      : location.rooms && location.rooms.length > 0
        ? location.rooms.reduce((max, room) => {
            const size = room.roomSize
              ? convertCmToMeters(room.roomSize)
              : room.maxArea
                ? convertCmToMeters(room.maxArea)
                : 0;
            return size > max ? size : max;
          }, 0)
        : 0
    : 0;

  const capacitySeated = location
    ? location?.capacitySeated && location.capacitySeated > 0
      ? location.capacitySeated
      : Math.max(
          location.seatingBanquet ?? 0,
          location.seatingBlock ?? 0,
          location.seatingParliamentary ?? 0,
          location.seatingRow ?? 0,
          location.seatingTable ?? 0,
          location.seatingUShape ?? 0,
        )
    : 0;

  // Check if rooms section should be shown
  const hasRooms =
    planLimits.maxRooms > 0 && (location?.rooms?.length ?? 0) > 0;

  // Check if hotel section should be shown
  const hasHotel =
    !!location?.accommodations &&
    (effectivePlan === "premium" || effectivePlan === "superior");

  // Check if downloads section should be shown (only for superior plan).
  // Includes certificates (Zertifizierungen / Mitgliedschaften) and floorplans/documents.
  const hasDownloads =
    (location?.certificates?.length ?? 0) > 0 ||
    (location?.floorplans?.length ?? 0) > 0 ||
    (location?.documents?.length ?? 0) > 0;
  const showDownloadsInNav = effectivePlan === "superior" && hasDownloads;
  const hasCertificatesSection =
    effectivePlan === "superior" && (location?.certificates?.length ?? 0) > 0;

  const locationName = location?.nameForm?.german || location?.name || "";
  const breadcrumbItems: {
    label: string;
    href?: string;
    search?: { location: string[] };
  }[] = [{ label: "Startseite", href: withLocale("/") }];
  if (location?.country) {
    breadcrumbItems.push({
      label: location.country,
      href: withLocale("/search"),
      search: { location: [location.country] },
    });
  }
  if (location?.state) {
    breadcrumbItems.push({
      label: getStateLabel(location.state),
      href: withLocale("/search"),
      search: { location: [location.state] },
    });
  }
  if (location?.city) {
    breadcrumbItems.push({
      label: location.city,
      href: withLocale("/search"),
      search: { location: [location.city] },
    });
  }
  breadcrumbItems.push({ label: locationName });

  return (
    <>
      {isPreviewMode ? (
        <div
          className="fixed top-4 right-4 z-50 pointer-events-none"
          aria-hidden
        >
          <div className="bg-amber-100 border-amber-500 border text-amber-900 shadow py-1.5 px-3 rounded-md">
            Vorschau
          </div>
        </div>
      ) : null}
      <ImageGrid
        groupedImages={groupedLocationImages ?? {}}
        plan={effectivePlan}
        primaryImageId={primaryImageId}
      />
      <nav
        aria-label="Breadcrumb"
        className="bg-white layout-place-full-width layout-content-grid"
      >
        <ol className="flex flex-wrap items-center gap-x-2 py-3 text-xs text-foreground">
          {breadcrumbItems.map((item, index) => (
            <li key={index} className="flex items-center gap-x-2">
              {index > 0 && (
                <span className="text-muted-foreground" aria-hidden="true">
                  &gt;
                </span>
              )}
              {item.href ? (
                <Link
                  to={item.href}
                  search={item.search}
                  className="hover:underline focus:outline-none focus:underline"
                >
                  {item.label}
                </Link>
              ) : (
                <span className="font-medium" aria-current="page">
                  {item.label}
                </span>
              )}
            </li>
          ))}
        </ol>
      </nav>
      <SectionNav
        showRooms={hasRooms}
        showHotel={hasHotel}
        showDownloads={showDownloadsInNav}
        actionButtons={
          showActionsInNav ? (
            <LocationActions
              variant="compact"
              isBookmarked={!!watchListEntry}
              onBookmark={() => {
                if (watchListEntry && location && location.id) {
                  removeLocationFromWatchListMutation.mutateAsync(location.id);
                } else if (!watchListEntry && location && location.id) {
                  addLocationToWatchListMutation.mutateAsync(location.id);
                }
              }}
              bookmarkingBusy={
                addLocationToWatchListMutation.isPending ||
                removeLocationFromWatchListMutation.isPending
              }
              onRequest={async () => {
                if (location?.id) {
                  await setInquiryLocation(location.id);
                  openInquiry("direct", [location.id]);
                }
              }}
              onShare={() => setShowShareDialog(true)}
            />
          ) : undefined
        }
      />
      {/* Mobile sticky bottom action bar */}
      <div
        className="md:hidden py-1.5 fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-dove-gray-light layout-place-full-width layout-content-grid"
        data-mobile-location-actions="true"
      >
        <div
          className="flex items-center justify-between gap-2 layout-place-outer-wrap"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          {watchListLocations?.length ? (
            <Button
              type="button"
              variant="black"
              size="icon"
              className="shrink-0 relative"
              onClick={() => openInquiry("watchlist", [])}
              aria-label="Merkliste öffnen"
              title="Merkliste"
            >
              <HeartFilledIcon className="size-5 text-primary" />
              <span className="tabular-nums absolute text-xs text-black top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                {watchListLocations?.length ?? 0}
              </span>
            </Button>
          ) : (
            <div></div>
          )}
          <div className="min-w-0 overflow-x-auto scrollbar-hide">
            <LocationActions
              className="gap-2"
              variant="compact"
              isBookmarked={!!watchListEntry}
              onBookmark={() => {
                if (watchListEntry && location && location.id) {
                  removeLocationFromWatchListMutation.mutateAsync(location.id);
                } else if (!watchListEntry && location && location.id) {
                  addLocationToWatchListMutation.mutateAsync(location.id);
                }
              }}
              bookmarkingBusy={
                addLocationToWatchListMutation.isPending ||
                removeLocationFromWatchListMutation.isPending
              }
              onRequest={async () => {
                if (location?.id) {
                  await setInquiryLocation(location.id);
                  openInquiry("direct", [location.id]);
                }
              }}
              onShare={() => setShowShareDialog(true)}
            />
          </div>
        </div>
      </div>
      <div className="lg:grid-cols-12 lg:gap-x-6 lg:grid">
        <div className="col-span-9">
          <ContentSection
            id="overview"
            title={location?.nameForm?.german || location?.name}
          >
            <LocationDetail
              rating={{ rating: 4, reviewsCount: 201 }}
              address={{
                country: location?.country ?? undefined,
                city: location?.city ?? undefined,
                postalCode: location?.postalCode ?? undefined,
                state: location?.state
                  ? getStateLabel(location.state)
                  : undefined,
                mapUrl: "/",
              }}
              stats={{
                insideAreaSum: insideAreaSum.toString(),
                outsideAreaSum: outsideAreaSum.toString(),
                outsideLargestArea:
                  location?.outsideLargestArea?.toString() ?? "0",
                totalRooms: totalRooms.toString(),
                capacitySeated: capacitySeated.toString(),
                capacityStanding: capacityStanding.toString(),
                biggestRoom: biggestRoom.toString(),
              }}
              description={
                location?.descriptionForm?.german || location?.description || ""
              }
            />
            {location?.locationTypes?.length ? (
              <SubSection title="Locationarten">
                <BadgeList
                  badges={(location?.locationTypes ?? [])
                    .map((t) => t.name)
                    .filter((n): n is string => n !== null)}
                />
              </SubSection>
            ) : null}
            {location?.eventTypes?.length ? (
              <SubSection title="Anlässe">
                <BadgeList
                  badges={(location?.eventTypes ?? [])
                    .map((t) => t.name)
                    .filter((n): n is string => n !== null)}
                />
              </SubSection>
            ) : null}
            {(() => {
              const locationSeating = {
                party: location?.seatingParty || null,
                row: location?.seatingRow || null,
                parliament: location?.seatingParliamentary || null,
                block: location?.seatingBlock || null,
                uform: location?.seatingUShape || null,
                banquet: location?.seatingBanquet || null,
                table: location?.seatingTable || null,
                standing: location?.seatingStanding || null,
              };

              const hasSeating = Object.values(locationSeating).some(
                (value) => value !== null && value !== 0,
              );

              if (!hasSeating) {
                return null;
              }

              return (
                <SubSection title="Bestuhlung">
                  <LocationSeatingCard
                    seating={locationSeating}
                    images={filteredImages}
                    indoorArea={
                      location?.indoorAreaTotal
                        ? convertCmToMeters(location.indoorAreaTotal)
                        : location?.insideAreaSum
                          ? convertCmToMeters(location.insideAreaSum)
                          : undefined
                    }
                  />
                </SubSection>
              );
            })()}
            {(() => {
              // Don't show rooms section if plan limits rooms to 0
              if (planLimits.maxRooms === 0) {
                return null;
              }

              type OrderType =
                | "size-asc"
                | "size-desc"
                | "standing-asc"
                | "standing-desc"
                | "row-asc"
                | "row-desc";

              const [order, setOrder] = useState<OrderType>("size-asc");
              const [expanded, setExpanded] = useState(false);

              const sorters: Record<OrderType, (a: any, b: any) => number> = {
                "size-asc": (a, b) =>
                  (Number(a.roomSize) || 0) - (Number(b.roomSize) || 0),
                "size-desc": (a, b) =>
                  (Number(b.roomSize) || 0) - (Number(a.roomSize) || 0),
                "standing-asc": (a, b) =>
                  (Number(a.seatingStanding) || 0) -
                  (Number(b.seatingStanding) || 0),
                "standing-desc": (a, b) =>
                  (Number(b.seatingStanding) || 0) -
                  (Number(a.seatingStanding) || 0),
                "row-asc": (a, b) =>
                  (Number(a.seatingRow) || 0) - (Number(b.seatingRow) || 0),
                "row-desc": (a, b) =>
                  (Number(b.seatingRow) || 0) - (Number(a.seatingRow) || 0),
              };

              // Apply plan-based room limit before sorting
              const availableRooms =
                planLimits.maxRooms === Infinity
                  ? (location?.rooms ?? [])
                  : (location?.rooms ?? []).slice(0, planLimits.maxRooms);

              const sortedRooms = [...availableRooms].sort(sorters[order]);

              const roomsToShow = expanded
                ? sortedRooms
                : sortedRooms.slice(0, 3);

              return (
                <SubSection
                  id="rooms"
                  title={`Räume (${availableRooms.length})`}
                >
                  <Select
                    value={order}
                    onValueChange={(val) => setOrder(val as OrderType)}
                  >
                    <SelectTrigger className="w-[280px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="size-asc">
                        Größe aufsteigend
                      </SelectItem>
                      <SelectItem value="size-desc">
                        Größe absteigend
                      </SelectItem>
                      {/* <SelectItem value="standing-asc">
                        Stehend aufsteigend
                      </SelectItem>
                      <SelectItem value="standing-desc">
                        Stehend absteigend
                      </SelectItem>
                      <SelectItem value="row-asc">
                        Reihenbestuhlung aufsteigend
                      </SelectItem>
                      <SelectItem value="row-desc">
                        Reihenbestuhlung absteigend
                      </SelectItem> */}
                    </SelectContent>
                  </Select>

                  {roomsToShow.map((room) => (
                    <RoomCard
                      key={room.id}
                      room={{
                        ...room,
                        images:
                          planLimits.maxImagesPerRoom === Infinity
                            ? room.images
                            : (room.images ?? []).slice(
                                0,
                                planLimits.maxImagesPerRoom,
                              ),
                      }}
                    />
                  ))}

                  {sortedRooms.length > 3 && (
                    <button
                      onClick={() => setExpanded((prev) => !prev)}
                      className="flex items-center gap-1 cursor-pointer text-dove-gray hover:text-neutral-darkest text-sm"
                    >
                      {expanded ? "Weniger anzeigen" : "Mehr anzeigen"}
                      <span
                        className={`${expanded ? "rotate-180" : ""} transition-transform`}
                      >
                        <ChevronDown />
                      </span>
                    </button>
                  )}
                </SubSection>
              );
            })()}

            {(() => {
              const outsideAreas = location?.outsideAreas ?? [];

              if (outsideAreas.length === 0 || effectivePlan === "basic") {
                return null;
              }

              type OrderType = "size-asc" | "size-desc";

              const [order, setOrder] = useState<OrderType>("size-asc");
              const [expanded, setExpanded] = useState(false);

              const sorters: Record<OrderType, (a: any, b: any) => number> = {
                "size-asc": (a, b) =>
                  (Number(a.maxArea) || 0) - (Number(b.maxArea) || 0),
                "size-desc": (a, b) =>
                  (Number(b.maxArea) || 0) - (Number(a.maxArea) || 0),
              };
              // Note: maxArea is in cm in the database, but sorting by raw value is fine

              const sortedOutsideAreas = [...outsideAreas].sort(sorters[order]);

              const outsideAreasToShow = expanded
                ? sortedOutsideAreas
                : sortedOutsideAreas.slice(0, 3);

              return (
                <SubSection
                  id="outsideAreas"
                  title={`Außenbereiche (${outsideAreas.length})`}
                >
                  <Select
                    value={order}
                    onValueChange={(val) => setOrder(val as OrderType)}
                  >
                    <SelectTrigger className="w-[280px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="size-asc">
                        Größe aufsteigend
                      </SelectItem>
                      <SelectItem value="size-desc">
                        Größe absteigend
                      </SelectItem>
                    </SelectContent>
                  </Select>

                  {outsideAreasToShow.map((outsideArea) => (
                    <OutsideAreaCard
                      key={outsideArea.id}
                      outsideArea={outsideArea}
                    />
                  ))}

                  {sortedOutsideAreas.length > 3 && (
                    <button
                      onClick={() => setExpanded((prev) => !prev)}
                      className="flex items-center gap-1 cursor-pointer text-dove-gray hover:text-neutral-darkest text-sm"
                    >
                      {expanded ? "Weniger anzeigen" : "Mehr anzeigen"}
                      <span
                        className={`${expanded ? "rotate-180" : ""} transition-transform`}
                      >
                        <ChevronDown />
                      </span>
                    </button>
                  )}
                </SubSection>
              );
            })()}

            {location?.accommodations &&
              (effectivePlan === "premium" || effectivePlan === "superior") && (
                <>
                  <SubSection id="hotel" title={"Hotel & Übernachtung"}>
                    {location?.hotelDescription ? (
                      <RichTextRenderer
                        richText={location.hotelDescription.german}
                      />
                    ) : null}

                    <HotelDetail
                      classification={location?.hotelStars ?? 0}
                      stats={{
                        totalRooms: location?.hotelRooms?.toString() ?? "0",
                        totalSingleRooms:
                          location?.hotelSingleRooms?.toString() ?? "0",
                        totalDoubleRooms:
                          location?.hotelDoubleRooms?.toString() ?? "0",
                        totalSharedRooms:
                          location?.hotelDorms?.toString() ?? "0",

                        freeWifi: location?.freeWifi ?? false,
                        freeLan: location?.freeLan ?? false,
                      }}
                    />

                    {location?.hotelTypes.length ? (
                      <FoldableBlock title="Hoteltyp">
                        <AmenitySection
                          items={
                            location?.hotelTypes.map((type) => {
                              return {
                                icon: <CheckIcon />,
                                text: type.name,
                              };
                            }) ?? []
                          }
                        />
                      </FoldableBlock>
                    ) : null}

                    {location?.reception24hours ||
                    location?.roomService ||
                    location?.wakeUpService ||
                    (location?.additionalAttributes?.some(
                      (attr) =>
                        attr.purpose === "location_hotel_service_attribute" &&
                        attr.active === true,
                    ) ??
                      false) ? (
                      <FoldableBlock title="Hotel-Services">
                        <AmenitySection
                          items={[
                            ...(location?.reception24hours
                              ? [
                                  {
                                    icon: <CheckIcon />,
                                    text: "Rezeption 24 h",
                                  },
                                ]
                              : []),
                            ...(location?.roomService
                              ? [
                                  {
                                    icon: <CheckIcon />,
                                    text: "Zimmerservice",
                                  },
                                ]
                              : []),
                            ...(location?.wakeUpService
                              ? [
                                  {
                                    icon: <CheckIcon />,
                                    text: "Weckservice",
                                  },
                                ]
                              : []),
                            ...(location?.additionalAttributes
                              ?.filter(
                                (attr) =>
                                  attr.purpose ===
                                    "location_hotel_service_attribute" &&
                                  attr.active === true,
                              )
                              .map((attr) => ({
                                icon: <CheckIcon />,
                                text: attr.name?.german ?? "",
                              })) ?? []),
                          ]}
                        />
                      </FoldableBlock>
                    ) : null}
                    {location?.conferenceOffice ||
                    location?.hotelSafe ||
                    location?.wifi ||
                    location?.lan ||
                    (location?.additionalAttributes?.some(
                      (attr) =>
                        attr.purpose === "location_hotel_facility_attribute" &&
                        attr.active === true,
                    ) ??
                      false) ? (
                      <FoldableBlock title="Hotelausstattung">
                        <AmenitySection
                          items={[
                            ...(location?.conferenceOffice
                              ? [{ icon: <CheckIcon />, text: "Tagungsbüro" }]
                              : []),
                            ...(location?.hotelSafe
                              ? [{ icon: <CheckIcon />, text: "Hotelsafe" }]
                              : []),
                            ...(location?.wifi
                              ? [{ icon: <CheckIcon />, text: "WLAN" }]
                              : []),
                            ...(location?.lan
                              ? [{ icon: <CheckIcon />, text: "LAN" }]
                              : []),
                            ...(location?.additionalAttributes
                              ?.filter(
                                (attr) =>
                                  attr.purpose ===
                                    "location_hotel_facility_attribute" &&
                                  attr.active === true,
                              )
                              .map((attr) => ({
                                icon: <CheckIcon />,
                                text: attr.name?.german ?? "",
                              })) ?? []),
                          ]}
                        />
                      </FoldableBlock>
                    ) : null}
                    {location?.roomsWithShowers ||
                    location?.roomsWithBath ||
                    location?.roomsWithAirConditioning ||
                    (location?.additionalAttributes?.some(
                      (attr) =>
                        attr.purpose ===
                          "location_hotel_room_facility_attribute" &&
                        attr.active === true,
                    ) ??
                      false) ? (
                      <FoldableBlock title="Zimmerausstattung">
                        <AmenitySection
                          items={[
                            ...(location?.roomsWithShowers
                              ? [
                                  {
                                    icon: <CheckIcon />,
                                    text: "Badezimmer mit Dusche",
                                  },
                                ]
                              : []),
                            ...(location?.roomsWithBath
                              ? [
                                  {
                                    icon: <CheckIcon />,
                                    text: "Badezimmer mit Badewanne",
                                  },
                                ]
                              : []),
                            ...(location?.roomsWithAirConditioning
                              ? [{ icon: <CheckIcon />, text: "Klimaanlage" }]
                              : []),
                            ...(location?.additionalAttributes
                              ?.filter(
                                (attr) =>
                                  attr.purpose ===
                                    "location_hotel_room_facility_attribute" &&
                                  attr.active === true,
                              )
                              .map((attr) => ({
                                icon: <CheckIcon />,
                                text: attr.name?.german ?? "",
                              })) ?? []),
                          ]}
                        />
                      </FoldableBlock>
                    ) : null}
                  </SubSection>
                  {(() => {
                    const [expanded, setExpanded] = useState(false);
                    const rooms = location?.hotelRoomInstances ?? [];
                    const roomsToShow = expanded ? rooms : rooms.slice(0, 3);

                    return (
                      <SubSection title="Unsere Zimmer">
                        {roomsToShow.map((hotelRoom) => (
                          <HotelRoomCard
                            key={hotelRoom.id}
                            hotelRoom={hotelRoom}
                          />
                        ))}
                        {rooms.length > 3 && (
                          <button
                            onClick={() => setExpanded((prev) => !prev)}
                            className="flex items-center gap-1 cursor-pointer text-dove-gray hover:text-neutral-darkest text-sm"
                          >
                            {expanded ? "Weniger anzeigen" : "Mehr anzeigen"}
                            <span
                              className={`${expanded ? "rotate-180" : ""} transition-transform`}
                            >
                              <ChevronDown />
                            </span>
                          </button>
                        )}
                      </SubSection>
                    );
                  })()}
                </>
              )}

            {effectivePlan === "superior" && locationVideos.length > 0 ? (
              <SubSection id="videos" title="Videos">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {locationVideos.map((video) => (
                    <div
                      key={video.id}
                      className="overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50"
                    >
                      <video
                        src={resolveAssetUrl(video.path)}
                        controls
                        className="w-full aspect-video object-contain"
                        preload="metadata"
                      >
                        Dein Browser unterstützt das Video-Tag nicht.
                      </video>
                    </div>
                  ))}
                </div>
              </SubSection>
            ) : null}

            {effectivePlan === "superior" && location?.virtualTour?.trim() ? (
              <SubSection id="virtual-tour" title="Virtuelle Tour">
                <div className="overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50 aspect-video w-full">
                  <iframe
                    src={location.virtualTour.trim()}
                    title="Virtuelle Tour"
                    className="h-full w-full min-h-[400px]"
                    allow="fullscreen; accelerometer; gyroscope; magnetometer; vr"
                    allowFullScreen
                  />
                </div>
              </SubSection>
            ) : null}

            <SubSection id="details" title="Detaildaten">
              <p>Loc.-ID: {location?.id}</p>
              {location?.descriptionLong ? (
                <RichTextRenderer richText={location.descriptionLong.german} />
              ) : null}

              {location?.openingYear ? (
                <div>
                  <strong>Eröffnungjahr</strong>
                  <p>{location.openingYear}</p>
                </div>
              ) : null}
              {location?.yearOfRenovation ? (
                <div>
                  <strong>Renovierung</strong>
                  <p>{location.yearOfRenovation}</p>
                </div>
              ) : null}
            </SubSection>
            {(() => {
              const pricingItems =
                location?.priceIsPublic === true
                  ? [
                      {
                        label: "Basismietpreis",
                        value: location?.rateDaily,
                        format: (v: number) =>
                          `${formatGermanCurrency(v / 100)} € / Tag`,
                      },
                      {
                        label: "Stundenlimit",
                        value: location?.rateDailyHourLimit,
                        format: (v: number) =>
                          v === 0
                            ? "Unbeschränkt"
                            : `${formatGermanNumberWithoutDecimals(v)} Std.`,
                      },
                      {
                        label: "Stundenpreis",
                        value: location?.rateHourly,
                        format: (v: number) =>
                          `${formatGermanCurrency(v / 100)} € / Stunde`,
                      },
                      {
                        label: "Mindeststunden",
                        value: location?.rateHourlyMinimumHours,
                        format: (v: number) =>
                          `${formatGermanNumberWithoutDecimals(v)} Std.`,
                      },
                      {
                        label:
                          location?.minimumConsumptionPersons === -1
                            ? "Mindestverzehr Pauschale"
                            : "Mindestverzehr pro Person",
                        value: location?.minimumConsumptionPrice,
                        format: (v: number) =>
                          `${formatGermanCurrency(v / 100)} €`,
                      },
                      {
                        label: "Mindestverzehr minimale Personenanzahl",
                        value: location?.minimumConsumptionPersons,
                        format: (v: number) =>
                          `${formatGermanNumberWithoutDecimals(v)} Personen`,
                      },
                      {
                        label: "Pauschale pro Person",
                        value: location?.consumptionFlatRatePerPerson,
                        format: (v: number) =>
                          `${formatGermanCurrency(v / 100)} €`,
                      },
                      {
                        label: "Pauschale Mindestpersonen",
                        value: location?.consumptionFlatRateMinimumPersons,
                        format: (v: number) => `${v} Personen`,
                      },
                    ].filter((item) => item.value != null && item.value > 0)
                  : [];

              const hasAdditionalPricingText =
                !!location?.additionalPricingInformation?.german?.trim();

              const shouldShowKonditionenSection =
                (location?.priceIsPublic === true &&
                  (pricingItems.length > 0 || hasAdditionalPricingText)) ||
                (location?.priceIsPublic !== true && hasAdditionalPricingText);

              if (!shouldShowKonditionenSection) {
                return null;
              }

              return (
                <SubSection title="Konditionen">
                  <p>
                    Alle Preise verstehen sich als „ab-Preise“ und sind
                    unverbindlich.
                  </p>
                  {location?.priceIsPublic === true ? (
                    <>
                      {pricingItems.length > 0 && (
                        <div className="space-y-2">
                          {pricingItems.map((item) => (
                            <div
                              key={item.label}
                              className="flex justify-between"
                            >
                              <strong>{item.label}</strong>
                              <span>{item.format(item.value!)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {hasAdditionalPricingText && (
                        <RichTextRenderer
                          richText={
                            location?.additionalPricingInformation?.german ?? ""
                          }
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex flex-1 max-w-[250px] items-center justify-between">
                        <strong>Preis auf Anfrage</strong>
                      </div>
                      {hasAdditionalPricingText && (
                        <RichTextRenderer
                          richText={
                            location?.additionalPricingInformation?.german ?? ""
                          }
                        />
                      )}
                    </>
                  )}
                </SubSection>
              );
            })()}
            <SubSection title="Vertragsbindungen">
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 items-start">
                <AmenitySection
                  title="Catering"
                  items={[
                    ...(location?.catering === "free"
                      ? [
                          {
                            icon: <CheckIcon />,
                            text: "freie Cateringwahl",
                          },
                        ]
                      : []),

                    ...(location?.catering === "permanentPartner"
                      ? [
                          {
                            icon: <CheckIcon />,
                            text: "Catering durch festen Partner",
                          },
                        ]
                      : []),

                    ...(location?.catering === "multiplePartner"
                      ? [
                          {
                            icon: <CheckIcon />,
                            text: "Catering durch mehrere Partner",
                          },
                        ]
                      : []),

                    ...(location?.catering === "freeChoicePaid"
                      ? [
                          {
                            icon: <CheckIcon />,
                            text: "Catering frei wählbar (gegen Gebühr)",
                          },
                        ]
                      : []),
                  ]}
                />
                <AmenitySection
                  title="Technik"
                  items={[
                    ...(location?.tech === "free"
                      ? [
                          {
                            icon: <CheckIcon />,
                            text: "freie Technikwahl",
                          },
                        ]
                      : []),

                    ...(location?.tech === "permanentPartner"
                      ? [
                          {
                            icon: <CheckIcon />,
                            text: "Technik durch festen Partner",
                          },
                        ]
                      : []),

                    ...(location?.tech === "multiplePartner"
                      ? [
                          {
                            icon: <CheckIcon />,
                            text: "Technik durch mehrere Partner",
                          },
                        ]
                      : []),

                    ...(location?.tech === "freeChoicePaid"
                      ? [
                          {
                            icon: <CheckIcon />,
                            text: "Technik frei wählbar (gegen Gebühr)",
                          },
                        ]
                      : []),
                  ]}
                />
                {(location?.additionalContractBindings ?? [])
                  .filter((binding) => binding.active === true)
                  .map((binding, idx) => (
                    <AmenitySection
                      key={(binding as any).id ?? idx}
                      title={binding?.name?.german?.trim() || "Vertragsbindung"}
                      items={[
                        ...(binding?.activeOption === "free"
                          ? [
                              {
                                icon: <CheckIcon />,
                                text: "Frei",
                              },
                            ]
                          : []),
                        ...(binding?.activeOption === "permanentPartner"
                          ? [
                              {
                                icon: <CheckIcon />,
                                text: "Fester Partner",
                              },
                            ]
                          : []),
                        ...(binding?.activeOption === "multiplePartner"
                          ? [
                              {
                                icon: <CheckIcon />,
                                text: "Mehrere Partner",
                              },
                            ]
                          : []),
                        ...(binding?.activeOption === "freeChoicePaid"
                          ? [
                              {
                                icon: <CheckIcon />,
                                text: "Freie Wahl möglich (gegen Gebühr)",
                              },
                            ]
                          : []),
                      ]}
                    />
                  ))}
              </div>
              {location?.contractDetails && location?.contractDetails.german ? (
                <div>
                  <strong>Sonstiges</strong>
                  <RichTextRenderer
                    richText={
                      location?.contractDetails?.german ?? "Keine Angabe"
                    }
                  />
                </div>
              ) : null}
            </SubSection>
            {location?.openingHours.length &&
            location?.rentalOutOfHoursPossible !== true ? (
              <SubSection title="Mietzeitraum">
                <ScheduleTable
                  schedule={formatOpeningHours(location?.openingHours as any[])}
                />
                <RichTextRenderer
                  richText={
                    location.additionalRentalInformation?.german ??
                    "Keine zusätzlichen Einschränkungen vom Anbieter."
                  }
                />
              </SubSection>
            ) : null}
            {location?.locationShortDescription?.german ||
            location?.infraTypes.length ||
            infrastructureItems.length ? (
              <SubSection title="Lage & Infrastruktur">
                {location?.locationShortDescription ? (
                  <RichTextRenderer
                    richText={location.locationShortDescription.german}
                  />
                ) : null}
                <BadgeList
                  badges={(location?.infraTypes ?? [])
                    .map((t) => t.name)
                    .filter((n): n is string => n !== null)}
                />
                <DistanceTable items={infrastructureItems} />
              </SubSection>
            ) : null}
            {(() => {
              const hasAdditionalInfo =
                location?.additionalParkingSpaceInformation?.german &&
                location.additionalParkingSpaceInformation.german.trim() !== "";

              // Don't show if parkingLots is "no" and there's no additional information
              if (location?.parkingLots === "no" && !hasAdditionalInfo) {
                return null;
              }

              // Show if parkingLots is set to something other than "no", or if there's additional info
              const shouldShow =
                location?.parkingLots === "yes" ||
                location?.parkingLots === "noOwn" ||
                hasAdditionalInfo;

              if (!shouldShow) return null;

              return (
                <SubSection title="Parkplätze">
                  <AmenitySection
                    items={[
                      ...(location?.parkingLots === "noOwn"
                        ? [
                            {
                              icon: <XIcon />,
                              text: "keine eigenen Parkplätze vorhanden",
                            },
                          ]
                        : []),

                      ...(location?.parkingLots === "no"
                        ? [
                            {
                              icon: <XIcon />,
                              text: "keine Parkplätze vorhanden",
                            },
                          ]
                        : []),
                      ...(location?.parkingLots === "yes"
                        ? [
                            {
                              icon: <CheckIcon />,
                              text: `eigene Parkplätze vorhanden: ${location.parkingSpaceCount && location.parkingSpaceCount > 0 ? location?.parkingSpaceCount : "/"}${location?.parkingLotRoof ? `, davon ${location?.coveredParkingSpaceCount} überdacht` : ""}`,
                            },
                          ]
                        : []),
                    ]}
                  />
                  {hasAdditionalInfo && (
                    <RichTextRenderer
                      richText={
                        location?.additionalParkingSpaceInformation?.german ??
                        ""
                      }
                    />
                  )}
                </SubSection>
              );
            })()}
            {(() => {
              const hasDeliveryLogistics =
                location?.accessibleForTrucks ||
                location?.accessibleForCars ||
                location?.freightElevator ||
                location?.loadingDock ||
                location?.additionalAttributes?.some(
                  (attr) =>
                    attr.purpose === "location_logistics_attribute" &&
                    attr.active === true,
                );

              if (!hasDeliveryLogistics) return null;

              return (
                <SubSection title="Anlieferung & Logistik">
                  <AmenitySection
                    items={[
                      // {
                      //   icon: location?.accessibleForTrucks ? (
                      //     <CheckIcon />
                      //   ) : (
                      //     <XIcon />
                      //   ),
                      //   text: "LKW (Location innen)",
                      // },
                      // {
                      //   icon: location?.accessibleForCars ? (
                      //     <CheckIcon />
                      //   ) : (
                      //     <XIcon />
                      //   ),
                      //   text: "PKW (Location innen)",
                      // },
                      {
                        icon: location?.freightElevator ? (
                          <CheckIcon />
                        ) : (
                          <XIcon />
                        ),
                        text: "Lastenaufzug",
                      },
                      {
                        icon: location?.loadingDock ? <CheckIcon /> : <XIcon />,
                        text: "Laderampe",
                      },
                      ...(location?.additionalAttributes
                        ?.filter(
                          (attr) =>
                            attr.purpose === "location_logistics_attribute" &&
                            attr.active === true,
                        )
                        .map((attr) => ({
                          icon: <CheckIcon />,
                          text: attr.name?.german ?? "",
                        })) ?? []),
                    ]}
                  />
                </SubSection>
              );
            })()}

            {(() => {
              const hasCommonProperties =
                location?.accessible ||
                location?.blackoutCapable ||
                location?.daylight ||
                (location?.additionalAttributes?.some(
                  (attr) =>
                    attr.purpose === "location_attribute" &&
                    attr.active === true,
                ) ??
                  false);

              if (!hasCommonProperties) return null;

              return (
                <SubSection title="Allgemeine Eigenschaften">
                  <AmenitySection
                    items={[
                      ...(location?.accessible
                        ? [
                            {
                              icon: <CheckIcon />,
                              text: "Barrierefrei",
                            },
                          ]
                        : []),
                      ...(location?.blackoutCapable
                        ? [
                            {
                              icon: <CheckIcon />,
                              text: "Verdunkelbar",
                            },
                          ]
                        : []),
                      ...(location?.daylight
                        ? [
                            {
                              icon: <CheckIcon />,
                              text: "Tageslicht",
                            },
                          ]
                        : []),
                      ...(location?.accessibleForCars
                        ? [
                            {
                              icon: <CheckIcon />,
                              text: "PKW-befahrbar (Location innen)",
                            },
                          ]
                        : []),
                      ...(location?.accessibleForTrucks
                        ? [
                            {
                              icon: <CheckIcon />,
                              text: "LKW-befahrbar (Location innen)",
                            },
                          ]
                        : []),
                      ...(location?.additionalAttributes
                        ?.filter(
                          (attr) =>
                            attr.purpose === "location_attribute" &&
                            attr.active === true,
                        )
                        .map((attr) => ({
                          icon: <CheckIcon />,
                          text: attr.name?.german ?? "",
                        })) ?? []),
                    ]}
                  ></AmenitySection>
                </SubSection>
              );
            })()}

            <SubSection title="Ausstattung">
              {(() => {
                const hasCatering =
                  location?.tableware === true ||
                  location?.kitchen === true ||
                  location?.cateringRoom === true ||
                  (location?.additionalAttributes?.some(
                    (attr) =>
                      attr.purpose === "location_catering_attribute" &&
                      attr.active === true,
                  ) ??
                    false);

                if (!hasCatering) return null;

                return (
                  <AmenitySection
                    icon={<CutleryIcon />}
                    title="Catering & Gastro"
                    items={[
                      ...(location?.tableware === true
                        ? [
                            {
                              icon: <CheckIcon />,
                              text: "Geschirr buchbar",
                            },
                          ]
                        : []),
                      ...(location?.kitchen === true
                        ? [
                            {
                              icon: <CheckIcon />,
                              text: "Küche",
                            },
                          ]
                        : []),
                      ...(location?.cateringRoom === true
                        ? [
                            {
                              icon: <CheckIcon />,
                              text: "spezieller Cateringraum",
                            },
                          ]
                        : []),
                      ...(location?.additionalAttributes
                        ?.filter(
                          (attr) =>
                            attr.purpose === "location_catering_attribute" &&
                            attr.active === true,
                        )
                        .map((attr) => ({
                          icon: <CheckIcon />,
                          text: attr.name?.german ?? "",
                        })) ?? []),
                    ]}
                  />
                );
              })()}
              {location?.bandWidth !== undefined &&
                location?.bandWidth !== null &&
                location.bandWidth > 0 && (
                  <AmenitySection
                    icon={<WifiIcon />}
                    title="Internet"
                    items={[
                      {
                        icon: <CheckIcon />,
                        text: `${location.bandWidth} Mbit/s`,
                      },
                    ]}
                  />
                )}
              {(() => {
                const hasMobileConnection =
                  location?.connection2G === true ||
                  location?.connection3G === true ||
                  location?.connection4G === true ||
                  location?.connection5G === true;

                if (!hasMobileConnection) return null;

                return (
                  <AmenitySection
                    icon={<CellularIcon />}
                    title="Mobilfunk"
                    items={[
                      ...(location?.connection2G
                        ? [
                            {
                              icon: <CheckIcon />,
                              text: "2G",
                            },
                          ]
                        : []),
                      ...(location?.connection3G
                        ? [
                            {
                              icon: <CheckIcon />,
                              text: "3G",
                            },
                          ]
                        : []),
                      ...(location?.connection4G
                        ? [
                            {
                              icon: <CheckIcon />,
                              text: "4G",
                            },
                          ]
                        : []),
                      ...(location?.connection5G
                        ? [
                            {
                              icon: <CheckIcon />,
                              text: "5G",
                            },
                          ]
                        : []),
                    ]}
                  />
                );
              })()}
              {(() => {
                const hasPower =
                  location?.v230 === true ||
                  location?.v110 === true ||
                  location?.a16 === true ||
                  location?.a32 === true ||
                  location?.a63 === true ||
                  location?.a128 === true;

                if (!hasPower) return null;

                return (
                  <AmenitySection
                    icon={<PowerIcon />}
                    title="Strom"
                    items={[
                      ...(location?.v230 === true || location?.v110 === true
                        ? [
                            {
                              icon: <CheckIcon />,
                              text: `Wechselstrom: ${[
                                location?.v230 === true ? "230 Volt" : null,
                                location?.v110 === true ? "110 Volt" : null,
                              ]
                                .filter(Boolean)
                                .join(", ")}`,
                            },
                          ]
                        : []),
                      ...(location?.a16 === true ||
                      location?.a32 === true ||
                      location?.a63 === true ||
                      location?.a128 === true
                        ? [
                            {
                              icon: <CheckIcon />,
                              text: `CEE-Drehstrom: ${[
                                location?.a16 === true ? "16A" : null,
                                location?.a32 === true ? "32A" : null,
                                location?.a63 === true ? "63A" : null,
                                location?.a128 === true ? "128A" : null,
                              ]
                                .filter(Boolean)
                                .join(", ")}`,
                            },
                          ]
                        : []),
                    ]}
                  />
                );
              })()}
              {(() => {
                const techItems = [
                  ...(location?.additionalAttributes
                    ?.filter(
                      (a) =>
                        a.purpose ===
                          "location_lighting_technology_attribute" && a.active,
                    )
                    .map((a) => ({
                      icon: <CheckIcon />,
                      text: a.name?.german ?? "",
                    })) ?? []),
                  ...(location?.additionalAttributes
                    ?.filter(
                      (a) =>
                        a.purpose ===
                          "location_presentation_technology_attribute" &&
                        a.active,
                    )
                    .map((a) => ({
                      icon: <CheckIcon />,
                      text: a.name?.german ?? "",
                    })) ?? []),
                  ...(location?.additionalAttributes
                    ?.filter(
                      (a) =>
                        a.purpose === "location_sound_technology_attribute" &&
                        a.active,
                    )
                    .map((a) => ({
                      icon: <CheckIcon />,
                      text: a.name?.german ?? "",
                    })) ?? []),
                  ...(location?.additionalAttributes
                    ?.filter(
                      (a) =>
                        a.purpose ===
                          "location_heating_and_air_conditioning_technology_attribute" &&
                        a.active,
                    )
                    .map((a) => ({
                      icon: <CheckIcon />,
                      text: a.name?.german ?? "",
                    })) ?? []),
                ];
                return techItems.length ? (
                  <AmenitySection
                    icon={<RadioIcon />}
                    title="Technik"
                    items={techItems}
                  />
                ) : null;
              })()}
              {(() => {
                const riggingItems =
                  location?.additionalAttributes?.filter(
                    (a) =>
                      a.purpose === "location_rigging_attribute" && a.active,
                  ) ?? [];
                return riggingItems.length ? (
                  <AmenitySection
                    icon={<GearIcon />}
                    title="Rigging"
                    items={riggingItems.map((a) => ({
                      icon: <CheckIcon />,
                      text: a.name?.german ?? "",
                    }))}
                  />
                ) : null;
              })()}
              {(() => {
                const specialItems =
                  location?.additionalAttributes?.filter(
                    (a) =>
                      a.purpose === "location_special_technology_attribute" &&
                      a.active,
                  ) ?? [];
                return specialItems.length ? (
                  <AmenitySection
                    icon={<GearIcon />}
                    title="Spezielle Technik"
                    items={specialItems.map((a) => ({
                      icon: <CheckIcon />,
                      text: a.name?.german ?? "",
                    }))}
                  />
                ) : null;
              })()}
              {(location as any)?.additionalTechnologyInformation?.german && (
                <section className="mt-4">
                  <h3 className="font-bold mb-2">
                    Sonstige wichtige Informationen zur Technik
                  </h3>
                  <RichTextRenderer
                    richText={
                      (location as any).additionalTechnologyInformation.german
                    }
                  />
                </section>
              )}
              {(() => {
                const internetItems =
                  location?.additionalAttributes?.filter(
                    (a) =>
                      a.purpose ===
                        "location_internet_availability_attribute" && a.active,
                  ) ?? [];
                return internetItems.length ? (
                  <AmenitySection
                    icon={<RadioIcon />}
                    title="Internetverfügbarkeit"
                    items={internetItems.map((a) => ({
                      icon: <CheckIcon />,
                      text: a.name?.german ?? "",
                    }))}
                  />
                ) : null;
              })()}
              <AmenitySection
                icon={<Lamp2Icon />}
                title="Mobiliar und Dekoration"
                items={[
                  ...(location?.furnishedForm === "no"
                    ? [
                        {
                          icon: <XIcon />,
                          text: "nicht vorhanden",
                        },
                      ]
                    : []),
                  ...(location?.furnishedForm === "yes"
                    ? [
                        {
                          icon: <CheckIcon />,
                          text: "Vorhanden",
                        },
                        ...(location?.amountOfChairs
                          ? [
                              {
                                icon: <CheckIcon />,
                                text: `${location.amountOfChairs} Stühle`,
                              },
                            ]
                          : []),
                        ...(location?.amountOfTables
                          ? [
                              {
                                icon: <CheckIcon />,
                                text: `${location.amountOfTables} Tische`,
                              },
                            ]
                          : []),
                      ]
                    : []),
                  ...(location?.furnishedForm === "bookable"
                    ? [
                        {
                          icon: <CheckIcon />,
                          text: "Vorhanden gegen Gebühr",
                        },
                        ...(location?.amountOfChairs
                          ? [
                              {
                                icon: <CheckIcon />,
                                text: `${location.amountOfChairs} Stühle`,
                              },
                            ]
                          : []),
                        ...(location?.amountOfTables
                          ? [
                              {
                                icon: <CheckIcon />,
                                text: `${location.amountOfTables} Tische`,
                              },
                            ]
                          : []),
                      ]
                    : []),
                  ...(!location?.furnishedForm &&
                  location?.furnished !== undefined
                    ? [
                        {
                          icon: location.furnished ? <CheckIcon /> : <XIcon />,
                          text: location.furnished
                            ? "Vorhanden"
                            : "nicht vorhanden",
                        },
                      ]
                    : []),
                  ...(location?.additionalAttributes
                    ?.filter(
                      (a) =>
                        a.purpose ===
                          "location_furniture_and_decoration_attribute" &&
                        a.active,
                    )
                    .map((a) => ({
                      icon: <CheckIcon />,
                      text: a.name?.german ?? "",
                    })) ?? []),
                ]}
              />
              {location?.stage === true && (
                <AmenitySection
                  icon={<StageIcon />}
                  title="Bühne"
                  items={[
                    {
                      icon: <CheckIcon />,
                      text: location.stageFixed
                        ? "Bühne vorhanden: fest verbaut"
                        : "Bühne vorhanden",
                    },
                    // ...(location.stageLength
                    //   ? [
                    //     {
                    //       icon: <CheckIcon />,
                    //       text: `Bühnenlänge: ${formatGermanNumberWithoutDecimals(
                    //         convertCmToMeters(location.stageLength),
                    //       )} m`,
                    //     },
                    //   ]
                    //   : []),
                    ...(location.stageWidth
                      ? [
                          {
                            icon: <CheckIcon />,
                            text: `Bühnenbreite: ${formatGermanNumberWithoutDecimals(
                              convertCmToMeters(location.stageWidth),
                            )} m`,
                          },
                        ]
                      : []),
                    ...(location.stageDepth
                      ? [
                          {
                            icon: <CheckIcon />,
                            text: `Bühnentiefe: ${formatGermanNumberWithoutDecimals(
                              convertCmToMeters(location.stageDepth),
                            )} m`,
                          },
                        ]
                      : []),
                    // ...(location.stageHeight
                    //   ? [
                    //     {
                    //       icon: <CheckIcon />,
                    //       text: `Bühnenhöhe: ${formatGermanNumberWithoutDecimals(
                    //         convertCmToMeters(location.stageHeight),
                    //       )} m`,
                    //     },
                    //   ]
                    //   : []),
                    ...(location.stageArea
                      ? [
                          {
                            icon: <CheckIcon />,
                            text: `Bühnenfläche: ${formatGermanNumberWithoutDecimals(
                              convertCmToMeters(location.stageArea),
                            )} m²`,
                          },
                        ]
                      : []),
                  ].filter(Boolean)}
                />
              )}
              {location?.additionalStageInformation?.german && (
                <RichTextRenderer
                  richText={location.additionalStageInformation.german}
                />
              )}
              {(() => {
                const sanitaryItems =
                  location?.additionalAttributes?.filter(
                    (a) =>
                      a.purpose === "location_sanitary_facility_attribute" &&
                      a.active,
                  ) ?? [];
                const additionalSanitary = (location as any)
                  ?.additionalSanitaryInformation?.german;
                if (sanitaryItems.length === 0 && !additionalSanitary)
                  return null;
                return (
                  <>
                    {sanitaryItems.length > 0 && (
                      <AmenitySection
                        title="Sanitäre Einrichtungen"
                        items={sanitaryItems.map((a) => ({
                          icon: <CheckIcon />,
                          text: a.name?.german ?? "",
                        }))}
                      />
                    )}
                    {additionalSanitary && (
                      <section className="mt-4">
                        <h3 className="font-bold mb-2">
                          Sonstige wichtige Informationen zu sanitären
                          Einrichtungen
                        </h3>
                        <RichTextRenderer richText={additionalSanitary} />
                      </section>
                    )}
                  </>
                );
              })()}
              {(() => {
                const equipmentItems =
                  location?.additionalAttributes?.filter(
                    (attr) =>
                      attr.purpose === "location_equipment_attribute" &&
                      attr.active === true,
                  ) ?? [];
                if (equipmentItems.length === 0) return null;
                return (
                  <AmenitySection
                    title="Sonstiges"
                    items={equipmentItems.map((attr) => ({
                      icon: <CheckIcon />,
                      text: attr.name?.german ?? "",
                    }))}
                  />
                );
              })()}
            </SubSection>
            {(() => {
              // Check if any leisure/sport/wellness values are set
              const hasLeisure =
                location?.sightseeing ||
                (location?.additionalAttributes?.some(
                  (attr) =>
                    attr.purpose === "location_leisure_time_attribute" &&
                    attr.active === true,
                ) ??
                  false);

              const hasSport =
                location?.golf ||
                location?.tennis ||
                location?.sailing ||
                location?.ski ||
                (location?.additionalAttributes?.some(
                  (attr) =>
                    attr.purpose === "location_sport_attribute" &&
                    attr.active === true,
                ) ??
                  false);

              const hasWellness =
                location?.spa ||
                location?.massage ||
                location?.swimmingIndoor ||
                location?.swimmingOutdoor ||
                (location?.additionalAttributes?.some(
                  (attr) =>
                    attr.purpose === "location_wellness_attribute" &&
                    attr.active === true,
                ) ??
                  false);

              const hasAdditionalInfo =
                location?.additionalLeisureInformation?.german &&
                location.additionalLeisureInformation.german.trim() !== "";

              const shouldShow =
                hasLeisure || hasSport || hasWellness || hasAdditionalInfo;

              if (!shouldShow) return null;

              return (
                <SubSection title="Freizeit / Sport / Wellness">
                  <AmenitySection
                    title="Freizeit"
                    items={[
                      ...(location?.sightseeing
                        ? [
                            {
                              icon: <CheckIcon />,
                              text: "Sightseeing",
                            },
                          ]
                        : []),
                      ...(location?.additionalAttributes
                        ?.filter(
                          (attr) =>
                            attr.purpose ===
                              "location_leisure_time_attribute" &&
                            attr.active === true,
                        )
                        .map((attr) => ({
                          icon: <CheckIcon />,
                          text: attr.name?.german ?? "",
                        })) ?? []),
                    ]}
                  ></AmenitySection>
                  <AmenitySection
                    title="Sport"
                    items={[
                      ...(location?.golf
                        ? [
                            {
                              icon: <CheckIcon />,
                              text: "Golf",
                            },
                          ]
                        : []),
                      ...(location?.tennis
                        ? [
                            {
                              icon: <CheckIcon />,
                              text: "Tennis",
                            },
                          ]
                        : []),
                      ...(location?.sailing
                        ? [
                            {
                              icon: <CheckIcon />,
                              text: "Segeln",
                            },
                          ]
                        : []),
                      ...(location?.ski
                        ? [
                            {
                              icon: <CheckIcon />,
                              text: "Ski",
                            },
                          ]
                        : []),
                      ...(location?.additionalAttributes
                        ?.filter(
                          (attr) =>
                            attr.purpose === "location_sport_attribute" &&
                            attr.active === true,
                        )
                        .map((attr) => ({
                          icon: <CheckIcon />,
                          text: attr.name?.german ?? "",
                        })) ?? []),
                    ]}
                  ></AmenitySection>
                  <AmenitySection
                    title="Wellness"
                    items={[
                      ...(location?.spa
                        ? [
                            {
                              icon: <CheckIcon />,
                              text: "Spa",
                            },
                          ]
                        : []),
                      ...(location?.massage
                        ? [
                            {
                              icon: <CheckIcon />,
                              text: "Massage",
                            },
                          ]
                        : []),
                      ...(location?.swimmingIndoor
                        ? [
                            {
                              icon: <CheckIcon />,
                              text: "Schwimmbad indoor",
                            },
                          ]
                        : []),
                      ...(location?.swimmingOutdoor
                        ? [
                            {
                              icon: <CheckIcon />,
                              text: "Schwimmbad outdoor",
                            },
                          ]
                        : []),
                      ...(location?.additionalAttributes
                        ?.filter(
                          (attr) =>
                            attr.purpose === "location_wellness_attribute" &&
                            attr.active === true,
                        )
                        .map((attr) => ({
                          icon: <CheckIcon />,
                          text: attr.name?.german ?? "",
                        })) ?? []),
                    ]}
                  ></AmenitySection>
                  {hasAdditionalInfo && (
                    <RichTextRenderer
                      richText={
                        location?.additionalLeisureInformation?.german ?? ""
                      }
                    />
                  )}
                </SubSection>
              );
            })()}
            {effectivePlan === "superior" &&
              (location?.certificates?.length ?? 0) > 0 && (
                <SubSection
                  title="Zertifizierungen / Mitgliedschaften"
                  id="certificates"
                >
                  <div className="flex flex-wrap gap-6">
                    {location?.certificates
                      .filter((cert) => cert.active === true)
                      .map((cert, index) => {
                        const path = cert?.file?.path ?? "";

                        if (path) {
                          return (
                            <div
                              key={
                                (cert as any).id ?? cert.name ?? path ?? index
                              }
                              className="inline-flex items-center justify-center w-[100px] h-[100px] border border-dove-gray-light rounded-md p-1 transition-transform duration-200 ease-out hover:scale-105"
                            >
                              <img
                                src={resolveAssetUrl(path)}
                                alt={cert.name || "Zertifikat"}
                                className="w-full h-full object-contain"
                              />
                            </div>
                          );
                        }

                        if (cert.name) {
                          return (
                            <div
                              key={(cert as any).id ?? cert.name ?? index}
                              className="inline-flex items-center justify-center px-3 py-2 border border-dove-gray-light rounded-md text-sm"
                            >
                              {cert.name}
                            </div>
                          );
                        }

                        return null;
                      })}
                  </div>
                </SubSection>
              )}
            {effectivePlan === "superior" &&
              ((location?.floorplans?.length ?? 0) > 0 ||
                (location?.documents?.length ?? 0) > 0) && (
                <SubSection
                  title="Downloads"
                  id={hasCertificatesSection ? undefined : "downloads"}
                >
                  <DownloadSection
                    items={[
                      ...(location?.floorplans ?? []).map((floorplan) => ({
                        text: (() => {
                          const path = floorplan?.path ?? "";
                          const match = path.match(/\/([^\/?#]+)(?:[?#].*)?$/);
                          return match ? match[1] : "";
                        })(),
                        url: resolveAssetUrl(floorplan?.path ?? ""),
                      })),
                      ...(location?.documents ?? []).map((document) => ({
                        text: (() => {
                          const path = document?.path ?? "";
                          const match = path.match(/\/([^\/?#]+)(?:[?#].*)?$/);
                          return match ? match[1] : "";
                        })(),
                        url: resolveAssetUrl(document?.path ?? ""),
                      })),
                    ]}
                  />
                </SubSection>
              )}
          </ContentSection>
        </div>
        <div className="col-span-3">
          <ContentSection>
            <SubSection>
              <div>
                {logo && effectivePlan === "superior" ? (
                  <div className="flex justify-center mb-8">
                    <img
                      src={resolveAssetUrl(logo.path ?? "")}
                      alt={logo.alt ?? ""}
                    />
                  </div>
                ) : null}
                <div ref={sidebarActionsRef}>
                  <LocationActions
                    isBookmarked={!!watchListEntry}
                    onBookmark={() => {
                      if (watchListEntry && location && location.id) {
                        removeLocationFromWatchListMutation.mutateAsync(
                          location.id,
                        );
                      } else if (!watchListEntry && location && location.id) {
                        addLocationToWatchListMutation.mutateAsync(location.id);
                      }
                    }}
                    bookmarkingBusy={
                      addLocationToWatchListMutation.isPending ||
                      removeLocationFromWatchListMutation.isPending
                    }
                    onRequest={async () => {
                      if (location?.id) {
                        await setInquiryLocation(location.id);
                        openInquiry("direct", [location.id]);
                      }
                    }}
                    onShare={() => setShowShareDialog(true)}
                  />
                </div>
              </div>
            </SubSection>
            <SubSection>
              <LocationContact
                imageUrl={
                  location?.images.find(
                    (image) =>
                      image.purpose === "location_contact_person_image",
                  )?.path ?? undefined
                }
                name={[
                  location?.contactPersonFirstName ?? "",
                  location?.contactPersonLastName ?? "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                email={location?.contactPersonEmail ?? undefined}
                phone={location?.contactPersonPhone ?? undefined}
                company={location?.nameForm?.german || location?.name}
                address={[
                  ...(location?.street && location?.houseNumber
                    ? [`${location.street} ${location.houseNumber}`]
                    : []),
                  ...(location?.postalCode && location?.city
                    ? [`${location.postalCode} ${location.city}`]
                    : []),
                  ...(location?.country ? [location?.country] : []),
                ]}
                website={location?.website ?? undefined}
                websites={(location as any)?.websites ?? undefined}
                socialInstagram={location?.socialInstagram ?? undefined}
                socialLinkedin={location?.socialLinkedin ?? undefined}
                socialProfiles={(location as any)?.socialProfiles ?? undefined}
                onImpressumClick={() => setImprintOpen(true)}
                showContactPerson={
                  effectivePlan === "superior" || effectivePlan === "premium"
                }
                showContactPersonImage={effectivePlan === "superior"}
                showLinks={
                  effectivePlan === "superior" || effectivePlan === "premium"
                }
              />
            </SubSection>
          </ContentSection>
        </div>
      </div>
      <ImprintPopUp
        open={imprintOpen}
        onOpenChange={setImprintOpen}
        location={location}
      />
      {location && (
        <ShareDialog
          open={showShareDialog}
          onOpenChange={setShowShareDialog}
          location={{
            name: location.name,
            images: location.images,
            locationTypes: location.locationTypes,
            descriptionForm: location.descriptionForm,
            description: location.description,
            priceLevel: location.priceLevel,
            capacityStanding: location.capacityStanding,
            capacitySeated: location.capacitySeated ?? null,
            seatingBanquet: location.seatingBanquet ?? null,
            seatingBlock: location.seatingBlock ?? null,
            seatingParliamentary: location.seatingParliamentary ?? null,
            seatingRow: location.seatingRow ?? null,
            seatingTable: location.seatingTable ?? null,
            seatingUShape: location.seatingUShape ?? null,
            seatingStanding: location.seatingStanding ?? null,
            city: location.city,
          }}
          shareUrl={shareUrl}
        />
      )}
      {location?.faq && location.faq.length > 0 && (
        <ContentSection>
          <FaqSection
            showButton={false}
            title="FAQ"
            description="Häufig gestellte Fragen"
            items={location.faq.map((f) => {
              return {
                title: f.question.german ?? "",
                description: f.answer.german ?? "",
              };
            })}
          />
        </ContentSection>
      )}
      {/* 
      <ContentSection>
        <Testimonial
          quote="Die Zusammenarbeit mit LocationGuide24 hat unsere Suche nach dem perfekten Drehort erheblich erleichtert. Die Plattform ist benutzerfreundlich und bietet eine Vielzahl an Optionen."
          name="Anna Müller"
          role="Produzentin, Filmhaus"
          avatar="/temp/anna-mustermann.png"
          companyLogo="/temp/leipziger-messe.svg"
        />
      </ContentSection>
      <CtaWideSimple
        title="Werde Teil unseres Netzwerks"
        description="Melden Sie sich an, um Ihre Locations zu listen und neue Kunden zu gewinnen."
        image={ctaWideImage}
        link1="#"
        link2="#"
        ctaLabel1="Jetzt anmelden"
        ctaLabel2="Mehr erfahren"
      />
      <ContentSection title="Ähnliche Locations / Locations in der Gegend">
        <CardSlider
          items={[
            <TeaserCard
              title="Location 1234"
              description="Hochzeitslocation mit Terrasse"
              image={image}
              link="#"
            >
              <p className="text-xl font-semibold flex items-center gap-1 mb-2">
                <Location />
                Regensburg
              </p>
            </TeaserCard>,
            <TeaserCard
              title="Location 1234"
              description="Hochzeitslocation mit Terrasse"
              image={image}
              link="#"
            >
              <p className="text-xl font-semibold flex items-center gap-1 mb-2">
                <Location />
                Berlin
              </p>
            </TeaserCard>,
            <TeaserCard
              title="Location 1234"
              description="Hochzeitslocation mit Terrasse"
              image={image}
              link="#"
            >
              <p className="text-xl font-semibold flex items-center gap-1 mb-2">
                <Location />
                Berlin
              </p>
            </TeaserCard>,
            <TeaserCard
              title="Location 1234"
              description="Hochzeitslocation mit Terrasse"
              image={image}
              link="#"
            >
              <p className="text-xl font-semibold flex items-center gap-1 mb-2">
                <Location />
                Berlin
              </p>
            </TeaserCard>,
            <TeaserCard
              title="Location 1234"
              description="Hochzeitslocation mit Terrasse"
              image={image}
              link="#"
            >
              <p className="text-xl font-semibold flex items-center gap-1 mb-2">
                <Location />
                Berlin
              </p>
            </TeaserCard>,
            <TeaserCard
              title="Location 1234"
              description="Hochzeitslocation mit Terrasse"
              image={image}
              link="#"
            >
              <p className="text-xl font-semibold flex items-center gap-1 mb-2">
                <Location />
                Berlin
              </p>
            </TeaserCard>,
          ]}
        />
      </ContentSection> */}
    </>
  );
}
